import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Save, BookOpen, ClipboardCheck, FileSignature, AlertTriangle, AlertCircle, CheckCircle2, Camera, Eye, Download, RotateCcw, ExternalLink, FileText, Settings2, MessageSquareText, ImagePlus, Layers, ChevronRight } from 'lucide-react';
import CompareLayoutsButton from '@/components/CompareLayoutsButton';
import ConditionScriptPicker from '@/components/ConditionScriptPicker';
import UndoToolbar from '@/components/UndoToolbar';
import IdChip from '@/components/IdChip';
import AreaDefectsAccordion, { LibraryEditModal } from '@/components/AreaDefectsAccordion';
import MultiDefectAuditPanel from '@/components/MultiDefectAuditPanel';
import ConditionScriptAuditPanel from '@/components/ConditionScriptAuditPanel';

// iter298i · Canonicalise the appraisal's free-text roof_type/brand to
// the `roof_key` value used by `multi_defect_scripts`. Same rules as
// AreaDefectsAccordion.canonicaliseRoofKey — small duplication is
// cheaper than extracting to a shared util for this one caller.
function canonicaliseRoofKey(raw) {
  const s = String(raw || '').toLowerCase();
  if (!s) return '';
  if (s.includes('modern conc')) return 'modern_concrete';
  if (s.includes('old conc'))    return 'old_concrete';
  if (s.includes('colortile') || s.includes('colourtile')) return 'color_tile';
  if (s.includes('decramastic') || s.includes('tin tile')) return 'decramastic';
  if (s.includes('clay'))        return 'clay_tile';
  if (s.includes('longrun') || s.includes('long run') || s.includes('iron')) return 'longrun_iron';
  if (s.includes('concrete'))    return 'modern_concrete';
  return '';
}

/**
 * Dedicated "Roof Appraisal" data-entry tab for the Appraisal Detail page.
 *
 * This is the form Trevor fills out on-site (or from photos) when conducting
 * a Roof Inspection Appraisal. Every field on this tab maps 1:1 onto the
 * fields the `/api/reports/detailed-roof-appraisal/{id}` PDF generator
 * already reads from the appraisal document:
 *
 *   • roof_condition          → Roof Condition section
 *   • roof_requirements       → Roof Requirements bullet list
 *   • appraisal_condition     → Overview / cover-letter narrative
 *   • {area}_condition (8x)   → Per-area condition blocks on the PDF
 *
 * Per-area defect counts come from `appraisal_components` (one row per
 * defect) — this tab shows the live count next to the area heading and
 * surfaces a quick-add button that links to the Components tab.
 *
 * Each per-area textarea has a "Pick from library" button that opens the
 * existing ConditionScriptPicker filtered by inspection_category so the
 * user can stack pre-written phrases instead of retyping every report.
 *
 * Save uses PATCH /appraisals/{id}/conditions (existing endpoint) so we
 * never round-trip the full appraisal document — just the changed fields.
 */

// (label, field-on-appraisal, component_type for defect count grouping,
//  ConditionScripts inspection_category for the library picker)
const AREAS = [
  { label: 'Ridge',     field: 'ridge_condition',     compKey: 'ridge',     scriptCategory: 'Ridging' },
  { label: 'Tile',      field: 'tile_condition',      compKey: 'tile',      scriptCategory: 'Tiles' },
  { label: 'Valley',    field: 'valley_condition',    compKey: 'valley',    scriptCategory: 'Valleys' },
  { label: 'Barge',     field: 'barge_condition',     compKey: 'barge',     scriptCategory: 'Barging' },
  { label: 'Spouting',  field: 'spouting_condition',  compKey: 'spouting',  scriptCategory: 'Spouting' },
  { label: 'Sheet',     field: 'sheet_condition',     compKey: 'sheet',     scriptCategory: 'Sheeting' },
  { label: 'Fastening', field: 'fastening_condition', compKey: 'fastening', scriptCategory: 'Fastenings' },
  { label: 'Flashing',  field: 'flashing_condition',  compKey: 'flashing',  scriptCategory: 'Flashings' },
];

/**
 * @param multiPick - When true the dropdown stacks/appends each pick onto the
 *   textarea (good for richer report types that combine 2-3 phrases per
 *   area). When false (default for the Roof Appraisal PDF) a pick
 *   REPLACES the textarea contents so only one comment surfaces per area
 *   in the final report. The current selection id is then surfaced in the
 *   dropdown so the user always knows which script is "live".
 */
export default function RoofAppraisalTab({ api, appraisalId, appraisal, roofs, onSaved, onJumpTab, multiPick = false, embedReturnToRunner = false }) {
  const [form, setForm] = useState({});
  const [components, setComponents] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerField, setPickerField] = useState(null); // {field, scriptCategory}
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [undoRefresh, setUndoRefresh] = useState(0);
  // iter303ak-fix9 · Auto-save baseline refs — declared UP HERE (not
  // buried below the hydration effect) so the appraisal-hydration
  // useEffect can seed them with the just-hydrated form JSON. Without
  // this, hydration flipped `form` from `{}` to the populated shape
  // which the auto-save useEffect then diffed against `{}` and treated
  // as a user edit — firing a save + window.close() in embed mode,
  // auto-closing the popup ~1.3s after it opened.
  const initialFormJsonRef = useRef(null);
  const lastSavedJsonRef = useRef(null);
  // Pre-scripted phrases pulled once per mount, indexed by inspection_category.
  // Drives the inline "Choose comment…" dropdown on each card so the user
  // can pick a phrase without opening the full library modal.
  const [scripts, setScripts] = useState({});
  // Per-field "currently selected script id" — only meaningful in single-
  // pick mode where it drives the dropdown's visible value. In multi-pick
  // mode the dropdown stays on its placeholder.
  const [selectedScriptId, setSelectedScriptId] = useState({});
  // Roof-level recommendation tag options (e.g. "Repairs and Future
  // Restoration", "Reroof", "Nothing Required") pulled from the backend
  // so admin-authored custom tags surface in the dropdown too.
  const [recommendationOptions, setRecommendationOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/condition-scripts/roof-condition-recommendations');
        if (!cancelled) setRecommendationOptions(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setRecommendationOptions([]); }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const roofType = roofs?.[0]?.roof_type || '';
  // iter298i — Canonical roof_key used by the library audit panels
  // (`multi_defect_scripts.roof_types` matches this shortlist).
  const roofKey = useMemo(() => canonicaliseRoofKey(roofType), [roofType]);

  // Lightweight client+property meta fetch — only used to render the quick-
  // reference block in the header. Both surfaces (the tab + the standalone
  // /roof-report page) keep their own copy of these, but pulling it here
  // means the tab is self-contained and doesn't depend on props beyond
  // the appraisal itself.
  const [clientMeta, setClientMeta] = useState({ name: '', client_no: '' });
  const [propertyMeta, setPropertyMeta] = useState({ address: '', property_no: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (appraisal?.client_id) {
          const { data: c } = await api.get(`/clients/${appraisal.client_id}`);
          if (!cancelled) {
            const nm = [c?.name_first, c?.name_last].filter(Boolean).join(' ') || c?.name_company || '';
            setClientMeta({ name: nm, client_no: c?.client_no || c?.access_id || '' });
          }
        } else if (!cancelled) {
          setClientMeta({ name: '', client_no: '' });
        }
      } catch { if (!cancelled) setClientMeta({ name: '', client_no: '' }); }
      try {
        if (appraisal?.property_id) {
          const { data: p } = await api.get(`/properties/${appraisal.property_id}`);
          if (!cancelled) {
            const addr = [p?.property_address_num, p?.property_address_street].filter(Boolean).join(' ');
            const full = [addr, p?.property_address_suburb, p?.property_address_city].filter(Boolean).join(', ');
            setPropertyMeta({ address: full, property_no: p?.property_no || p?.access_id || '' });
          }
        } else if (!cancelled) {
          setPropertyMeta({ address: '', property_no: '' });
        }
      } catch { if (!cancelled) setPropertyMeta({ address: '', property_no: '' }); }
    })();
    return () => { cancelled = true; };
  }, [api, appraisal?.client_id, appraisal?.property_id]);

  // Hydrate form from the latest appraisal data — only the condition fields
  // we manage on this tab — so other tabs editing the same appraisal don't
  // step on each other.
  useEffect(() => {
    const hydrated = {
      roof_condition: appraisal?.roof_condition || '',
      roof_requirements: appraisal?.roof_requirements || '',
      roof_recommendation: appraisal?.roof_recommendation || '',
      appraisal_condition: appraisal?.appraisal_condition || '',
      ridge_condition: appraisal?.ridge_condition || '',
      tile_condition: appraisal?.tile_condition || '',
      valley_condition: appraisal?.valley_condition || '',
      barge_condition: appraisal?.barge_condition || '',
      sheet_condition: appraisal?.sheet_condition || '',
      spouting_condition: appraisal?.spouting_condition || '',
      fastening_condition: appraisal?.fastening_condition || '',
      flashing_condition: appraisal?.flashing_condition || '',
      // Per-area defect quantities — manually entered by the inspector on
      // the Roof Appraisal form. Empty (blank) means "not yet recorded";
      // 0 is a valid value (e.g. "no defects found in this area").
      ridge_defect_qty: appraisal?.ridge_defect_qty ?? '',
      tile_defect_qty: appraisal?.tile_defect_qty ?? '',
      valley_defect_qty: appraisal?.valley_defect_qty ?? '',
      barge_defect_qty: appraisal?.barge_defect_qty ?? '',
      sheet_defect_qty: appraisal?.sheet_defect_qty ?? '',
      spouting_defect_qty: appraisal?.spouting_defect_qty ?? '',
      fastening_defect_qty: appraisal?.fastening_defect_qty ?? '',
      flashing_defect_qty: appraisal?.flashing_defect_qty ?? '',
    };
    setForm(hydrated);
    // iter303ak-fix9 · Seed auto-save baseline with the hydrated form so
    // the diff-detector below doesn't treat hydration as a user edit.
    // In embedReturnToRunner mode, a spurious save fires window.close()
    // 400ms later → the Runner's poller auto-ticks the "reviewed"
    // checkbox and advances Action 3 before the operator can look at
    // the form. This line closes that race by baselining refs to the
    // just-hydrated JSON.
    const json = JSON.stringify(hydrated);
    initialFormJsonRef.current = json;
    lastSavedJsonRef.current = json;
    // Reset tracked selections on appraisal change — they will be re-derived
    // below once the scripts library has finished loading.
    setSelectedScriptId({});
  }, [appraisal]);

  // Once both the appraisal data AND the scripts library are loaded, try to
  // match each textarea against a known script body so the dropdown can
  // reflect "this is the script that's currently in the textarea" in
  // single-pick mode after a page reload.
  useEffect(() => {
    if (multiPick) return;
    const all = Object.values(scripts).flat();
    if (all.length === 0) return;
    const next = {};
    const FIELDS = [
      'roof_condition', 'roof_requirements', 'appraisal_condition',
      'ridge_condition', 'tile_condition', 'valley_condition', 'barge_condition',
      'spouting_condition', 'sheet_condition', 'fastening_condition', 'flashing_condition',
    ];
    FIELDS.forEach(f => {
      const txt = (form[f] || '').trim();
      if (!txt) return;
      const hit = all.find(s => ((s.body || s.body_text || '').trim()) === txt);
      if (hit) next[f] = hit._id;
    });
    setSelectedScriptId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scripts, multiPick, appraisal]);


  const loadComponents = useCallback(async () => {
    try {
      const { data } = await api.get(`/appraisal-components/${appraisalId}`);
      setComponents(Array.isArray(data) ? data : []);
    } catch { setComponents([]); }
  }, [api, appraisalId]);
  useEffect(() => { loadComponents(); }, [loadComponents]);

  // Load the pre-scripted comments once per mount and group by category so
  // each card can show only its own dropdown options. Empty roof_type
  // entries are kept as "generic" phrases applicable to every roof.
  const loadScripts = useCallback(async () => {
    try {
      const { data } = await api.get('/condition-scripts');
      const list = Array.isArray(data) ? data : [];
      const grouped = {};
      list.forEach(s => {
        const cat = s.inspection_category || s.category || '';
        if (!cat) return;
        (grouped[cat] = grouped[cat] || []).push(s);
      });
      // Sort each bucket by sort_order then short name so the dropdown
      // mirrors the Access form's ordering.
      Object.keys(grouped).forEach(k => {
        grouped[k].sort((a, b) => {
          const oa = a.sort_order ?? 0, ob = b.sort_order ?? 0;
          if (oa !== ob) return oa - ob;
          return (a.name || '').localeCompare(b.name || '');
        });
      });
      setScripts(grouped);
    } catch { setScripts({}); }
  }, [api]);
  useEffect(() => { loadScripts(); }, [loadScripts]);

  // Filter dropdown options by the resolved roof type — keep generic scripts
  // (no roof_type set) so the picker always has some baseline options.
  // Honours both the legacy single `roof_type` field AND the newer
  // `roof_types[]` array so users can flag a phrase as applying to many
  // roof types at once.
  const optionsFor = useCallback((category) => {
    const all = scripts[category] || [];
    if (!roofType) return all;
    const needle = roofType.toLowerCase();
    const matchesRT = (rt) => {
      if (!rt) return false;
      const v = String(rt).toLowerCase();
      return v.includes(needle) || needle.includes(v);
    };
    return all.filter(s => {
      const single = (s.roof_type || '').toLowerCase();
      const many = Array.isArray(s.roof_types) ? s.roof_types : [];
      // Generic phrases (no roof_type AND no roof_types) always appear.
      if (!single && many.length === 0) return true;
      if (matchesRT(single)) return true;
      return many.some(matchesRT);
    });
  }, [scripts, roofType]);

  // Group defect counts so each area heading can show "Ridges: 3" live.
  const defectCounts = useMemo(() => {
    const counts = {};
    components.forEach(c => {
      const t = (c.component_type || '').toLowerCase();
      counts[t] = (counts[t] || 0) + (parseInt(c.quantity, 10) || 1);
    });
    return counts;
  }, [components]);

  const setField = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    // If the user manually edits a field that had a "selected script", clear
    // the dropdown's tracked selection so the picker doesn't keep claiming
    // a script that no longer matches the textarea.
    setSelectedScriptId(prev => (prev[k] ? { ...prev, [k]: '' } : prev));
  };

  // iter303ak-fix12 · Trevor: "selecting a dropdown auto-closed the popup
  // and advanced the runner. This should only happen on manual Save &
  // Return click." Auto-saves still fire silently on user edits (900ms
  // debounce) — they just don't close the window. Only the explicit
  // Save & Return button (which passes closeAfter=true) hops back to
  // the runner. All auto-save call-sites use the default (false).
  const save = async (closeAfter = false) => {
    // iter303ak-fix12 · Photo-check gate on Save & Return.
    // For multi_defect reports, photos are strongly preferred but not
    // compulsory (Trevor: "This is a multi defect + photos report, so it
    // should have photos"). Comments + defects ARE required — those are
    // validated by the report generator, but the operator-facing safety
    // net here is the "any areas with defects but zero photos?" check.
    // Show the operator exactly which areas are missing photos so they
    // can decide to go back and attach, or push through anyway.
    if (closeAfter && mode === 'multi_defect') {
      const areasWithDefectsButNoPhotos = AREAS.filter(a => {
        const arr = defectsByArea[a.compKey] || [];
        return arr.length > 0 && !arr.some(d => d && (d.photo_url || d.photo_id));
      });
      if (areasWithDefectsButNoPhotos.length > 0) {
        const names = areasWithDefectsButNoPhotos.map(a => a.label).join(', ');
        const ok = window.confirm(
          `Photos are missing for: ${names}\n\n` +
          `Multi-Defect reports are stronger with photos, but they're not compulsory.\n\n` +
          `OK  — save & return anyway (no photos for these areas)\n` +
          `Cancel — stay here and go add photos`
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      await api.patch(`/appraisals/${appraisalId}/conditions`, form);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
      setUndoRefresh(k => k + 1);
      onSaved?.();
      if (embedReturnToRunner && closeAfter) {
        setTimeout(() => { try { window.close(); } catch { /* browser blocked */ } }, 400);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  // iter298o — Auto-save narratives + defect-qty fields when the operator
  // stops typing for 900ms. Multi-defect rows already save on their own
  // per-row modals, but the 8 per-area textareas, the 3 subforms, and the
  // qty inputs previously required a manual "Save all" click. Missing
  // that click was the number-one cause of "my edits didn't persist"
  // confusion. Guard rails:
  //   • initialFormJsonRef / lastSavedJsonRef are seeded by the hydration
  //     effect above (iter303ak-fix9) so hydration ≠ user edit.
  //   • Skip when a save is already in flight (`saving`) so overlapping
  //     network requests don't compete.
  //   • Compare stringified `form` against last-saved snapshot so
  //     re-hydration on tab switch doesn't spam the API with no-op saves.
  useEffect(() => {
    const current = JSON.stringify(form || {});
    if (initialFormJsonRef.current === null) {
      // Should never happen (hydration effect seeds this) but guard anyway.
      initialFormJsonRef.current = current;
      lastSavedJsonRef.current = current;
      return;
    }
    if (current === lastSavedJsonRef.current) return;  // no-op change (e.g. re-hydration)
    if (saving) return;                                 // save already in flight
    const t = setTimeout(() => {
      lastSavedJsonRef.current = current;
      save();
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const openPicker = (field, scriptCategory) => {
    setPickerField({ field, scriptCategory });
    setPickerOpen(true);
  };

  // The picker emits onInsert(text, mode, script) where mode is
  // 'append' or 'replace' and script is the full picked object (used to
  // auto-fill the roof-level recommendation tag when the user is
  // filling the Roof Condition field).
  const handlePickerInsert = (text, mode, script) => {
    if (!pickerField || !text) { setPickerOpen(false); return; }
    setForm(prev => {
      const existing = prev[pickerField.field] || '';
      const nextText = mode === 'replace' ? text : (existing ? `${existing.trim()}\n\n${text}` : text);
      const next = { ...prev, [pickerField.field]: nextText };
      if (pickerField.field === 'roof_condition' && script) {
        const rec = (script.recommendation || '').trim();
        if (rec && !(prev.roof_recommendation || '').trim()) {
          next.roof_recommendation = rec;
        }
      }
      return next;
    });
    setPickerOpen(false);
  };

  const resetField = (field) => {
    setForm(prev => ({ ...prev, [field]: '' }));
    setSelectedScriptId(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // Insert a pre-scripted phrase (by id) into the given field.
  //   • multiPick=true  → appends to the existing text (stack behaviour)
  //   • multiPick=false → REPLACES the existing text and tracks the pick in
  //                       `selectedScriptId` so the dropdown reflects the
  //                       current selection. This is the default for the
  //                       Roof Appraisal report — only one comment per
  //                       area surfaces on the PDF.
  // Empty value (placeholder) is a no-op so the dropdown can reset itself.
  const insertScriptIntoField = (field, scriptId) => {
    if (!scriptId) return;
    const all = Object.values(scripts).flat();
    const s = all.find(x => x._id === scriptId);
    if (!s) return;
    const body = (s.body || s.body_text || '').trim();
    if (!body) return;
    setForm(prev => {
      const next = { ...prev };
      if (multiPick) {
        const existing = (prev[field] || '').trim();
        next[field] = existing ? `${existing}\n\n${body}` : body;
      } else {
        next[field] = body;
      }
      // Auto-fill the roof-level recommendation tag when a Roof
      // Condition script is picked and the appraisal doesn't have one
      // set yet. Never clobber a user-chosen tag.
      if (field === 'roof_condition') {
        const rec = (s.recommendation || '').trim();
        if (rec && !(prev.roof_recommendation || '').trim()) {
          next.roof_recommendation = rec;
        }
      }
      return next;
    });
    if (!multiPick) {
      setSelectedScriptId(prev => ({ ...prev, [field]: scriptId }));
    }
  };

  const downloadPdf = async () => {
    // Phase 1 rename: Instant Roof Report (free / auto-generated).
    // Opens the generated PDF in a new browser tab (native PDF viewer with
    // print/download built-in). If the popup is blocked we DO NOT
    // `window.location.href = blobUrl` — that used to nuke the current
    // tab (double-navigation bug). Instead we surface a friendly hint.
    let blobUrl = null;
    try {
      const res = await api.get(`/reports/instant-roof-report/${appraisalId}`, { responseType: 'blob' });
      blobUrl = URL.createObjectURL(res.data);
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        alert('Preview blocked by your browser. Please allow pop-ups for this site and try again.');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      // Keep the blob URL alive long enough for the new tab to render.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      alert('PDF preview failed');
    }
  };

  const [detailedBusy, setDetailedBusy] = useState(false);

  // ── Roof-appraisal entry mode ────────────────────────────────────────
  // "one_comment" → shows the Per-area conditions grid (each area gets a
  //                 single textarea; feeds Instant Free + Detailed 1-Comment PDFs).
  // "multi_defect" → shows the Area Defects accordion (rich defect log
  //                  with photos; feeds the Detailed Multi-Defect PDF).
  // Empty string → user has not chosen yet; render the picker screen.
  const [mode, setMode] = useState(appraisal?.appraisal_mode || '');
  const [modeSaving, setModeSaving] = useState(false);
  // Cross-mode data indicator — count how many multi-defect entries exist
  // so Mode A can flag "you have data hiding on the other side" without
  // forcing the user to switch to see it.
  const [multiDefectCount, setMultiDefectCount] = useState(0);
  // iter303ak-fix12 · Per-area defect payload — powers the Completeness
  // panel below the footer (defect counts + photo counts per area).
  const [defectsByArea, setDefectsByArea] = useState({});
  useEffect(() => {
    setMode(appraisal?.appraisal_mode || '');
  }, [appraisal?.appraisal_mode]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/defects`);
        // Endpoint shape: `{appraisal_id: str, defects: { ridge: [...],
        // tile: [...], ... }}` — sum the per-area arrays. Fall back to a
        // flat-array shape defensively in case a future refactor flips it.
        const byArea = data?.defects || {};
        const total = Array.isArray(data)
          ? data.length
          : Object.values(byArea).reduce(
              (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
              0,
            );
        if (!cancelled) {
          setMultiDefectCount(total);
          setDefectsByArea(byArea);
        }
      } catch {
        if (!cancelled) { setMultiDefectCount(0); setDefectsByArea({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [api, appraisalId, mode, undoRefresh]);

  // Persist mode change immediately (independent of the "Save all" flow
  // so the operator's choice sticks even if they navigate away without
  // saving other fields).
  const chooseMode = async (nextMode) => {
    if (nextMode === mode) return;
    setModeSaving(true);
    try {
      await api.patch(`/appraisals/${appraisalId}/conditions`, { appraisal_mode: nextMode });
      setMode(nextMode);
      onSaved?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save mode');
    } finally {
      setModeSaving(false);
    }
  };

  // Count of filled per-area condition textareas — used by Mode B's
  // cross-mode badge so the operator sees at a glance whether Mode A
  // has data too.
  const oneCommentFilledCount = useMemo(() => {
    return AREAS.filter(a => (form[a.field] || '').trim().length > 0).length;
  }, [form]);
  const downloadDetailedAppraisal = async () => {
    // Mode B preview — Detailed Multi-Defect variant of the appraisal PDF.
    // Opens in a new tab (native PDF viewer). If the popup is blocked we
    // surface a hint rather than nuking the current tab.
    setDetailedBusy(true);
    let blobUrl = null;
    try {
      const res = await api.get(`/reports/detailed-roof-appraisal/${appraisalId}`, {
        responseType: 'blob',
        params: { variant: 'detailed_multi' },
      });
      blobUrl = URL.createObjectURL(res.data);
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        alert('Preview blocked by your browser. Please allow pop-ups for this site and try again.');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      let msg = 'Multi-Defect PDF preview failed.';
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail?.message) msg = parsed.detail.message;
          else if (parsed?.detail) msg = String(parsed.detail);
        }
      } catch { /* keep default fallback message */ }
      alert(msg);
    } finally {
      setDetailedBusy(false);
    }
  };

  return (
    <div data-testid="roof-appraisal-tab" className="space-y-5">
      {/* Prerequisite banner — the legacy Access workflow requires the
          Property to have Roof Details (with a roof_type) recorded BEFORE
          the appraisal narrative is filled in, because the prescripted
          dropdowns are filtered by roof_type. If the property has no roof
          details, surface a prominent CTA to fix that first. */}
      {(!roofs || roofs.length === 0 || !roofType) && (
        <div data-testid="roof-appraisal-prereq-banner" className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">Add property roof details first</p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              {roofs && roofs.length === 0
                ? 'This property has no roof details recorded yet.'
                : 'A Roof Type must be set on the property before the prescripted comment dropdowns can short-list relevant phrases.'}
              {' '}The appraisal will work without roof details, but every dropdown will show all phrases instead of the ones that match the roof type — slower to scan and easier to pick a wrong-roof comment.
            </p>
            {appraisal?.property_id && (
              <a
                data-testid="roof-appraisal-add-roof-details"
                href={`/properties/${appraisal.property_id}`}
                className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-amber-900 hover:text-amber-700 underline underline-offset-2"
              >
                Open property and add roof details <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Tab intro / context bar */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#316536]" />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-md bg-emerald-50 flex-shrink-0"><FileSignature size={18} className="text-[#316536]" /></div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Inspection data entry</p>
              <h2 className="text-base font-bold text-slate-900 font-[Manrope]">Roof Appraisal</h2>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">Fields below feed the <span className="font-mono-num text-slate-700">Instant Roof Report</span> PDF. Roof type: <span className="font-semibold text-slate-700">{roofType || <span className="italic text-slate-400">(not set — add roof details first)</span>}</span>.</p>
              <p data-testid="pick-mode-indicator" className="text-[11px] mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border" style={{
                color: multiPick ? '#1E40AF' : '#316536',
                backgroundColor: multiPick ? '#EFF6FF' : '#ECFDF5',
                borderColor: multiPick ? '#BFDBFE' : '#A7F3D0',
              }}>
                <span className="font-bold uppercase tracking-[0.12em]">{multiPick ? 'Multi-pick' : 'Single pick'}</span>
                <span className="text-slate-500">·</span>
                <span>{multiPick ? 'Dropdown stacks every selection' : 'One comment per area — picks replace the current text'}</span>
              </p>
            </div>
          </div>
          {/* Quick-reference block: client → property → appraisal IDs.
              Renders even when meta is empty so the layout doesn't jump
              as the data loads. */}
          <div data-testid="roof-appraisal-quick-ref" className="flex flex-col items-start sm:items-end gap-1 text-xs text-slate-600 min-w-[240px]">
            <div data-testid="qr-client" className="flex items-baseline gap-2 max-w-xs">
              {clientMeta.name ? (
                <a href={`/clients/${appraisal?.client_id}`} className="font-bold text-slate-900 hover:text-[#D35400] transition-colors truncate" title={clientMeta.name}>{clientMeta.name}</a>
              ) : <span className="italic text-slate-400">No client</span>}
              {clientMeta.client_no && (
                <IdChip type="client" value={clientMeta.client_no} size="sm" testId="qr-client-id" className="flex-shrink-0" />
              )}
            </div>
            <div data-testid="qr-property" className="flex items-baseline gap-2 max-w-md">
              {propertyMeta.address ? (
                <a href={`/properties/${appraisal?.property_id}`} className="text-slate-600 hover:text-[#D35400] transition-colors truncate" title={propertyMeta.address}>{propertyMeta.address}</a>
              ) : <span className="italic text-slate-400">No property</span>}
              {propertyMeta.property_no && (
                <IdChip type="property" value={propertyMeta.property_no} size="sm" testId="qr-property-id" className="flex-shrink-0" />
              )}
            </div>
            <div data-testid="qr-appraisal" className="flex items-baseline gap-2">
              <span className="text-slate-500 text-[11px]">Appraisal</span>
              <IdChip type="appraisal" value={appraisal?.appraisal_no || appraisal?.access_id || appraisalId.slice(-6)} size="md" testId="qr-appraisal-id" />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <UndoToolbar
              appraisalId={appraisalId}
              scope="conditions"
              refreshKey={undoRefresh}
              onRestored={() => { onSaved?.(); setUndoRefresh(k => k + 1); }}
              testIdPrefix="roof-appraisal-undo"
              compact={false}
            />
            {savedAt && <span className="text-[11px] text-emerald-600 font-semibold">Saved ✓</span>}
            {mode === 'one_comment' && (
              <button data-testid="roof-appraisal-download-pdf" onClick={downloadPdf} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-[#316536] text-[#316536] hover:bg-[#316536] hover:text-white rounded-md transition-all">
                <ExternalLink size={12} /> Preview Instant Report
              </button>
            )}
            {mode === 'multi_defect' && (
              <>
                <button data-testid="roof-appraisal-download-multi-pdf" onClick={downloadDetailedAppraisal} disabled={detailedBusy} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-purple-600 text-purple-700 hover:bg-purple-600 hover:text-white rounded-md transition-all disabled:opacity-50">
                  <FileText size={12} /> {detailedBusy ? 'Generating…' : 'Preview Multi-Defect Report'}
                </button>
                <CompareLayoutsButton appraisalId={appraisalId} variant="detailed_multi" size="compact" />
              </>
            )}
            {/* Direct link to the Instant Report script editor, pre-scoped to
                the current appraisal's roof-type via ?roof_type=. Uses a
                plain <a> (not react-router <Link>) so target="_blank" opens
                cleanly in a new tab without also triggering client-side
                navigation on the current page (which was causing a
                double-navigation bug). */}
            <a
              data-testid="roof-appraisal-edit-scripts-link"
              href={`/report-setup?tab=instant_report${roofType ? `&roof_type=${encodeURIComponent(roofType)}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Edit Instant Report scripts, section order & visibility"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-500 hover:text-[#316536] hover:bg-slate-100 rounded-md transition-all"
            >
              <Settings2 size={12} /> Edit scripts
            </a>
            <button data-testid="roof-appraisal-save" onClick={() => save(true)} disabled={saving} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold bg-[#D35400] hover:bg-[#A04000] text-white rounded-md disabled:opacity-50 transition-all">
              <Save size={12} strokeWidth={2.5} /> {saving ? 'Saving…' : (embedReturnToRunner ? 'Save & Return to Runner' : 'Save all')}
            </button>
          </div>
        </div>
      </div>

      <InstantFreeSwitch appraisal={appraisal} appraisalId={appraisalId} />

      {/* ── Entry-mode picker ─────────────────────────────────────────────
          Shown once, when the appraisal has no `appraisal_mode` set yet.
          Forces the operator to pick a workflow up-front instead of
          scrolling past both subforms. Mode is stored on the appraisal
          doc via PATCH /conditions so anyone opening the same appraisal
          lands on the same subform. */}
      {!mode && (
        <div data-testid="mode-picker-screen" className="bg-white border border-[#E2E2DF] rounded-md p-6">
          <div className="mb-5 flex items-start gap-3">
            <div className="p-2 rounded-md bg-slate-100 flex-shrink-0"><Layers size={20} className="text-slate-700" /></div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 font-[Manrope] leading-tight">Choose the appraisal entry mode</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Pick the workflow that fits this job. Your choice is saved on the appraisal — you can switch modes at any time and no data is lost when you do.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              data-testid="pick-mode-one-comment"
              onClick={() => chooseMode('one_comment')}
              disabled={modeSaving}
              className="group text-left border-2 border-[#E2E2DF] hover:border-[#316536] rounded-lg p-5 bg-emerald-50/20 hover:bg-emerald-50/60 transition-all disabled:opacity-60"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-md bg-emerald-100"><MessageSquareText size={20} className="text-[#316536]" strokeWidth={2.5} /></div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-[#316536] transition-colors mt-1" />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 mb-1">Fast · Free PDF</p>
              <h4 className="text-base font-bold text-slate-900 font-[Manrope] leading-tight">One Comment per Area Appraisal</h4>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                Records a single condition summary for each of the 8 roof areas (Ridge, Tile, Valley, Barge, Spouting, Sheet, Fastening, Flashing). Pick a phrase from the library or type your own. Perfect for the lead-generation <span className="font-semibold text-slate-800">Instant Free Report</span>.
              </p>
              {multiDefectCount > 0 && (
                <p className="text-[10px] mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> This appraisal already has {multiDefectCount} multi-defect entries logged — they stay saved on the other mode.
                </p>
              )}
            </button>
            <button
              data-testid="pick-mode-multi-defect"
              onClick={() => chooseMode('multi_defect')}
              disabled={modeSaving}
              className="group text-left border-2 border-[#E2E2DF] hover:border-purple-600 rounded-lg p-5 bg-purple-50/20 hover:bg-purple-50/60 transition-all disabled:opacity-60"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-md bg-purple-100"><ImagePlus size={20} className="text-purple-700" strokeWidth={2.5} /></div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-purple-600 transition-colors mt-1" />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-purple-700 mb-1">Detailed · Paid PDF</p>
              <h4 className="text-base font-bold text-slate-900 font-[Manrope] leading-tight">Multi-Defect & Photo Appraisal</h4>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                Full defect log per area — capture multiple issues per area with individual descriptions, recommendations and photos. Feeds the <span className="font-semibold text-slate-800">Detailed Multi-Defect Report</span> PDF.
              </p>
              {oneCommentFilledCount > 0 && (
                <p className="text-[10px] mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> {oneCommentFilledCount} per-area comment{oneCommentFilledCount === 1 ? '' : 's'} already recorded — they stay saved on the other mode.
                </p>
              )}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 italic mt-4">Tip: you can change the mode later using the toggle that appears at the top of this tab.</p>
        </div>
      )}

      {/* ── Mode segmented control (only shown once a mode is picked) ── */}
      {mode && (
        <div data-testid="mode-segmented-control" className="bg-white border border-[#E2E2DF] rounded-md p-3 flex flex-wrap items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Entry mode</span>
          <div className="flex gap-1 bg-slate-100 rounded-md p-1">
            <button
              data-testid="mode-tab-one_comment"
              onClick={() => chooseMode('one_comment')}
              disabled={modeSaving}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${mode === 'one_comment' ? 'bg-[#316536] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <MessageSquareText size={12} /> One Comment per Area
            </button>
            <button
              data-testid="mode-tab-multi_defect"
              onClick={() => chooseMode('multi_defect')}
              disabled={modeSaving}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${mode === 'multi_defect' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <ImagePlus size={12} /> Multi-Defect & Photo
            </button>
          </div>
          {/* Cross-mode data indicator — reassures the operator that the
              hidden side's data is intact. */}
          {mode === 'one_comment' && multiDefectCount > 0 && (
            <span data-testid="cross-mode-badge-multi" className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> {multiDefectCount} multi-defect entries saved on the other mode
            </span>
          )}
          {mode === 'multi_defect' && oneCommentFilledCount > 0 && (
            <span data-testid="cross-mode-badge-one" className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> {oneCommentFilledCount} per-area comment{oneCommentFilledCount === 1 ? '' : 's'} saved on the other mode
            </span>
          )}
          {modeSaving && <span className="text-[10px] text-slate-400 italic">Saving mode…</span>}
        </div>
      )}

      {/* Per-area condition grid — FIRST per user spec: this is what the
          inspector records on the roof, area by area. Only shown when the
          operator has chosen the "One Comment per Area" entry mode. */}
      {mode === 'one_comment' && (
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">Per-area conditions</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Defect counts come from the Components tab — record each defect there so the PDF can summarise them.{' '}
              {roofType ? (
                <span data-testid="roof-appraisal-filter-active">Dropdowns are filtered to <span className="font-bold text-[#316536]">{roofType}</span> + generic phrases.</span>
              ) : (
                <span data-testid="roof-appraisal-filter-inactive" className="text-amber-700">No roof type set — dropdowns show all phrases (less precise).</span>
              )}
            </p>
          </div>
          {onJumpTab ? (
            <button data-testid="roof-appraisal-jump-components" onClick={() => onJumpTab?.('Components')} className="text-[11px] text-[#316536] hover:text-[#234725] font-semibold flex items-center gap-1"><AlertTriangle size={11} /> Manage defect components →</button>
          ) : (
            <a data-testid="roof-appraisal-jump-components-link" href={`/appraisals/${appraisalId}?tab=Components`} className="text-[11px] text-[#316536] hover:text-[#234725] font-semibold flex items-center gap-1"><AlertTriangle size={11} /> Manage defect components →</a>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {AREAS.map(area => (
            <AreaCard
              key={area.field}
              area={area}
              count={defectCounts[area.compKey] || 0}
              opts={optionsFor(area.scriptCategory)}
              form={form}
              setForm={setForm}
              setField={setField}
              resetField={resetField}
              openPicker={openPicker}
              insertScriptIntoField={insertScriptIntoField}
              multiPick={multiPick}
              selectedScriptId={selectedScriptId}
              roofKey={roofKey}
              api={api}
              onScriptsChanged={loadScripts}
            />
          ))}
        </div>
      </div>
      )}

      {/* Phase 2 — Multi-defect per area. Only shown in "Multi-Defect &
          Photo" entry mode. Feeds the Detailed Multi-Defect PDF. */}
      {mode === 'multi_defect' && (
        <AreaDefectsAccordion appraisalId={appraisalId} roofType={roofType} />
      )}

      {/* Summary + narrative blocks — hidden until an entry mode is
          picked so the user isn't overwhelmed by a wall of textareas
          on first entry. Both modes show these once selected because
          the fields feed every PDF variant.

          User-requested order (Feb 2026): Roof Condition FIRST, then
          Appraisal Cover Narrative, then Roof Requirements — each
          stacked full-width one under the other for readability. */}
      {mode && (
        <>
          {/* 1️⃣ Roof Condition — the overall summary block. */}
          <div>
            <FieldCard
              title="Roof Condition"
              help="Overall summary of the roof's condition — printed on the PDF under 'Roof Condition:'."
              field="roof_condition"
              value={form.roof_condition || ''}
              rows={6}
              onChange={setField}
              onLibrary={() => openPicker('roof_condition', 'Roof Condition')}
              onReset={resetField}
              scriptOptions={optionsFor('Roof Condition')}
              onInsertScript={insertScriptIntoField}
              multiPick={multiPick}
              selectedScriptId={selectedScriptId['roof_condition'] || ''}
              auditArea={{ key: 'roof_condition', label: 'Roof Condition' }}
              roofKey={roofKey}
              api={api}
            />
            {/* Roof-level recommendation tag. Auto-filled from the picked
                Roof Condition script's `recommendation` field, editable
                here for tweaks. Printed on the Detailed PDF as
                "Recommendation:" beneath the Roof Condition paragraph. */}
            <div
              data-testid="roof-recommendation-block"
              className="mt-2 flex items-center gap-2 flex-wrap px-3 py-2 bg-emerald-50/60 border border-emerald-100 rounded-md"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Recommendation</span>
              {form.roof_recommendation && (
                <span
                  data-testid="roof-recommendation-badge"
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#316536] text-white"
                >
                  {form.roof_recommendation}
                </span>
              )}
              <select
                data-testid="roof-recommendation-select"
                value={form.roof_recommendation || ''}
                onChange={(e) => setField('roof_recommendation', e.target.value)}
                className="ml-auto px-2 py-1 text-xs border border-emerald-200 rounded-md bg-white"
              >
                <option value="">— none —</option>
                {recommendationOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 2️⃣ Appraisal Cover Narrative — the intro paragraph. */}
          <FieldCard
            title="Appraisal Cover Narrative"
            help="The introductory paragraph at the top of the inspection report. Replaces the auto-generated template intro if filled in."
            field="appraisal_condition"
            value={form.appraisal_condition || ''}
            rows={4}
            onChange={setField}
            onLibrary={() => openPicker('appraisal_condition', 'Appraisal Condition')}
            onReset={resetField}
            scriptOptions={optionsFor('Appraisal Condition')}
            onInsertScript={insertScriptIntoField}
            multiPick={multiPick}
            selectedScriptId={selectedScriptId['appraisal_condition'] || ''}
            auditArea={{ key: 'appraisal_narrative', label: 'Appraisal Cover Narrative' }}
            roofKey={roofKey}
            api={api}
          />

          {/* 3️⃣ Roof Requirements — the recommended-works bullet list. */}
          <FieldCard
            title="Roof Requirements"
            help="Bullet-list of recommended works. Each new line becomes a bullet in the PDF."
            field="roof_requirements"
            value={form.roof_requirements || ''}
            rows={6}
            onChange={setField}
            onLibrary={() => openPicker('roof_requirements', 'Roof Recommendation')}
            onReset={resetField}
            scriptOptions={optionsFor('Roof Recommendation')}
            onInsertScript={insertScriptIntoField}
            multiPick={multiPick}
            selectedScriptId={selectedScriptId['roof_requirements'] || ''}
            auditArea={{ key: 'roof_requirements', label: 'Roof Requirements' }}
            roofKey={roofKey}
            api={api}
          />

          {/* iter303ak-fix12 · Appraisal Completeness panel + Preview
              button. Renders before the footer save bar so the operator
              gets a clear "what's filled / what's missing" summary and
              can preview the PDF before hitting Save & Return. Trevor:
              "provide the same review and logic to see what appraisal
              data is entered or missing." */}
          {(() => {
            const cellClass = (ok) => ok
              ? 'flex items-center gap-1 text-emerald-700'
              : 'flex items-center gap-1 text-rose-700';
            const IconOk = () => <CheckCircle2 size={11} strokeWidth={2.5} />;
            const IconMissing = () => <AlertCircle size={11} strokeWidth={2.5} />;
            const hasComment = (field) => (form[field] || '').trim().length > 0;
            const defectQty = (field) => Number(form[`${field.replace('_condition','')}_defect_qty`] || 0);
            const areaDefects = (a) => (defectsByArea[a.compKey] || []);
            const areaPhotos = (a) => areaDefects(a).filter(d => d && (d.photo_url || d.photo_id)).length;
            const commentsFilled = AREAS.filter(a => hasComment(a.field)).length;
            const defectsPresent = AREAS.filter(a => areaDefects(a).length > 0).length;
            const photosPresent = AREAS.filter(a => areaPhotos(a) > 0).length;
            const isMulti = mode === 'multi_defect';
            const isOne = mode === 'one_comment';
            const previewHref = `${(process.env.REACT_APP_BACKEND_URL || '')}/api/reports/appraisal/${appraisalId}`;
            return (
              <div className="border-2 border-slate-200 rounded-md bg-slate-50/70 p-4" data-testid="roof-appraisal-completeness-panel">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck size={14} className="text-[#316536]" />
                    <div className="text-sm font-semibold text-slate-800">Appraisal Completeness</div>
                    <span className="text-[10px] font-medium text-slate-500 uppercase tracking-widest ml-2">
                      {isMulti ? 'Multi-Defect Report' : isOne ? 'Single-Defect Report' : 'No mode set'}
                    </span>
                  </div>
                  <a
                    href={previewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="roof-appraisal-preview-pdf"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-800 rounded-md transition-all"
                    title="Open the Appraisal Report PDF in a new tab"
                  >
                    <Eye size={12} strokeWidth={2.5} /> Preview Report PDF
                  </a>
                </div>

                {/* Appraisal cover narrative — required at the top of the report */}
                <div className="mb-3">
                  <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest mb-1">Appraisal Cover Narrative (required)</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                    {[
                      { key: 'roof_condition',      label: 'Roof Condition' },
                      { key: 'roof_requirements',   label: 'Roof Requirements' },
                      { key: 'roof_recommendation', label: 'Recommendation' },
                    ].map(f => {
                      const filled = hasComment(f.key);
                      return (
                        <div key={f.key} className={cellClass(filled)} data-testid={`completeness-narrative-${f.key}`}>
                          {filled ? <IconOk /> : <IconMissing />}
                          <span className="truncate">{f.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Per-area grid: comments always shown; defects + photos only for multi_defect */}
                <div className={`grid gap-4 text-xs ${isMulti ? 'md:grid-cols-3' : 'md:grid-cols-1'}`}>
                  {/* Column 1 — Comments (required) */}
                  <div>
                    <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest mb-1">
                      Comments <span className="text-rose-600 font-bold">(required)</span> — {commentsFilled}/{AREAS.length}
                    </div>
                    <div className="space-y-0.5">
                      {AREAS.map(a => {
                        const ok = hasComment(a.field);
                        return (
                          <div key={a.field} className={cellClass(ok)} data-testid={`completeness-comment-${a.compKey}`}>
                            {ok ? <IconOk /> : <IconMissing />}
                            <span>{a.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Column 2 — Defects (multi_defect only, required) */}
                  {isMulti && (
                    <div>
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest mb-1">
                        Defects <span className="text-rose-600 font-bold">(required)</span> — {defectsPresent}/{AREAS.length}
                      </div>
                      <div className="space-y-0.5">
                        {AREAS.map(a => {
                          const n = areaDefects(a).length;
                          const ok = n > 0;
                          return (
                            <div key={a.compKey} className={cellClass(ok)} data-testid={`completeness-defects-${a.compKey}`}>
                              {ok ? <IconOk /> : <IconMissing />}
                              <span>{a.label} {ok ? `· ${n}` : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Column 3 — Photos (multi_defect only, preferred) */}
                  {isMulti && (
                    <div>
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest mb-1 flex items-center gap-1">
                        <Camera size={11} />
                        Photos <span className="text-amber-600 font-bold">(preferred)</span> — {photosPresent}/{AREAS.length}
                      </div>
                      <div className="space-y-0.5">
                        {AREAS.map(a => {
                          const nDef = areaDefects(a).length;
                          const nPh = areaPhotos(a);
                          if (nDef === 0) {
                            return (
                              <div key={a.compKey} className="flex items-center gap-1 text-slate-400" data-testid={`completeness-photos-${a.compKey}`}>
                                <span className="text-[10px]">—</span>
                                <span>{a.label} · no defects</span>
                              </div>
                            );
                          }
                          const ok = nPh > 0;
                          return (
                            <div key={a.compKey} className={ok ? 'flex items-center gap-1 text-emerald-700' : 'flex items-center gap-1 text-amber-700'} data-testid={`completeness-photos-${a.compKey}`}>
                              {ok ? <IconOk /> : <AlertTriangle size={11} strokeWidth={2.5} />}
                              <span>{a.label} · {nPh}/{nDef} defect{nDef === 1 ? '' : 's'} with photos</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Single-defect quantities column */}
                  {isOne && (
                    <div>
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest mb-1">
                        Defect Quantities (optional — enter 0 if none)
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {AREAS.map(a => {
                          const q = defectQty(a.field);
                          return (
                            <div key={a.compKey} className="flex items-center gap-1 text-slate-700" data-testid={`completeness-qty-${a.compKey}`}>
                              <span className="text-slate-400">•</span>
                              <span>{a.label} · {q}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {isMulti && photosPresent < defectsPresent && defectsPresent > 0 && (
                  <div className="mt-3 pt-2 border-t border-amber-200 text-[11px] text-amber-800 bg-amber-50 rounded px-2 py-1.5 flex items-center gap-1.5">
                    <AlertTriangle size={12} />
                    <span>Photos missing for {defectsPresent - photosPresent} area{defectsPresent - photosPresent === 1 ? '' : 's'}. You'll be asked to confirm on Save &amp; Return.</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Sticky-ish footer save bar (only useful when scrolled deep) */}
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4 flex items-center justify-between">
            <p className="text-xs text-slate-500"><ClipboardCheck size={14} className="inline -mt-0.5 mr-1 text-[#316536]" /> All inspection fields above feed the Detailed Roof Appraisal PDF.</p>
            <div className="flex items-center gap-2">
              {savedAt && <span className="text-[11px] text-emerald-600 font-semibold">Saved ✓</span>}
              <button data-testid="roof-appraisal-save-bottom" onClick={() => save(true)} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-[#D35400] hover:bg-[#A04000] text-white rounded-md disabled:opacity-50 transition-all">
                <Save size={12} strokeWidth={2.5} /> {saving ? 'Saving…' : (embedReturnToRunner ? 'Save & Return to Runner' : 'Save all fields')}
              </button>
            </div>
          </div>
        </>
      )}

      <ConditionScriptPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onInsert={handlePickerInsert}
        defaultRoofType={roofType}
        inspectionCategory={pickerField?.scriptCategory || ''}
      />
    </div>
  );
}

function FieldCard({ title, help, field, value, rows, onChange, onLibrary, onReset, scriptOptions, onInsertScript, multiPick, selectedScriptId, auditArea, roofKey, api }) {
  // iter298i — optional audit panel toggle. When `auditArea` is provided,
  // a "📚 View all" button appears in the header, and toggling it splits
  // the card into a 2-column grid (form left, library audit right) so
  // the operator can sort/edit the script library without leaving the
  // appraisal page.
  const [showAudit, setShowAudit] = useState(false);
  const cardBody = (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">{title}</h3>
        <div className="flex items-center gap-1">
          {auditArea && (
            <button
              type="button"
              onClick={() => setShowAudit(v => !v)}
              className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md transition-colors border ${showAudit ? 'bg-purple-600 text-white border-purple-700' : 'text-purple-700 border-purple-400 hover:bg-purple-50'}`}
              data-testid={`audit-toggle-${field}`}
              title="View / edit / sort the full library for this field"
            >
              📚 {showAudit ? 'Hide' : 'View all'}
            </button>
          )}
          <button data-testid={`library-${field}`} onClick={onLibrary} className="flex items-center gap-1 text-[11px] px-2 py-1 border border-slate-200 hover:border-[#316536] hover:text-[#316536] rounded-md text-slate-600 transition-colors">
            <BookOpen size={10} /> Library
          </button>
          {value && (
            <button data-testid={`reset-${field}`} onClick={() => onReset(field)} title="Clear" className="p-1 text-slate-400 hover:text-rose-500 transition-colors"><RotateCcw size={12} /></button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">{help}</p>
      {scriptOptions && scriptOptions.length > 0 && onInsertScript && (
        <div className="mb-2">
          <select
            data-testid={`script-${field}`}
            value={multiPick ? '' : (selectedScriptId || '')}
            onChange={(e) => { onInsertScript(field, e.target.value); if (multiPick) e.target.value = ''; }}
            className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white focus:outline-none focus:border-[#316536]"
          >
            <option value="">{multiPick ? 'Add' : 'Choose'} comment… ({scriptOptions.length} options)</option>
            {scriptOptions.map(s => (
              <option key={s._id} value={s._id}>{s.name}{s.roof_type ? ` · ${s.roof_type}` : ''}</option>
            ))}
          </select>
        </div>
      )}
      <textarea
        data-testid={`field-${field}`}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm leading-relaxed focus:outline-none focus:border-[#316536]"
      />
    </div>
  );

  // Split-view mode: wrap the card in a 2-col grid alongside the audit
  // panel. `xl:` breakpoint keeps mobile screens single-column.
  if (auditArea && showAudit) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {cardBody}
        <MultiDefectAuditPanel
          area={auditArea}
          api={api}
          currentRoofKey={roofKey}
          onScriptsChanged={() => { /* library-only edits — no textarea refetch */ }}
          LibraryEditModal={LibraryEditModal}
        />
      </div>
    );
  }
  return cardBody;
}

// ---------------------------------------------------------------------------
// AreaCard — one of the 8 per-area cards in the "One Comment per Area"
// entry mode (Ridge, Tile, Valley, Barge, Sheet, Spouting, Fastening,
// Flashing). Extracted from the previous inline map so each card can
// own a `showAudit` toggle without violating the rules of hooks.
//
// When [📚 View all] is toggled, the card splits into a 2-col grid with
// a ConditionScriptAuditPanel on the right — the operator can then
// manually view, add, delete and edit the legacy `condition_scripts`
// library entries for THIS area (same UX as MultiDefectAuditPanel).
// ---------------------------------------------------------------------------
function AreaCard({ area, count, opts, form, setForm, setField, resetField, openPicker, insertScriptIntoField, multiPick, selectedScriptId, roofKey, api, onScriptsChanged }) {
  const [showAudit, setShowAudit] = useState(false);
  const qtyField = `${area.compKey}_defect_qty`;
  const qtyVal = form[qtyField];

  const cardBody = (
    <div data-testid={`area-${area.compKey}`} className="border border-[#E2E2DF] rounded-md p-3.5 bg-slate-50/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-bold text-slate-800">{area.label}</h4>
          {count > 0 && (
            <span data-testid={`defect-count-${area.compKey}`} title="Auto-counted from Components tab" className="text-[10px] uppercase tracking-[0.1em] font-bold rounded-full px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200">
              {count} component{count === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowAudit(v => !v)}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md transition-colors border ${showAudit ? 'bg-emerald-700 text-white border-emerald-800' : 'text-emerald-800 border-emerald-400 hover:bg-emerald-50'}`}
            data-testid={`view-all-${area.compKey}`}
            title={`View / add / edit / sort every ${area.label} defect script`}
          >
            📚 {showAudit ? 'Hide' : `View all '${area.label}' Defects`}
          </button>
          <button data-testid={`pick-${area.compKey}`} onClick={() => openPicker(area.field, area.scriptCategory)} className="flex items-center gap-1 text-[11px] px-2 py-1 border border-slate-200 hover:border-[#316536] hover:text-[#316536] rounded-md text-slate-600 transition-colors">
            <BookOpen size={10} /> Library
          </button>
          {form[area.field] && (
            <button data-testid={`reset-${area.compKey}`} onClick={() => resetField(area.field)} title="Clear this field" className="p-1 text-slate-400 hover:text-rose-500 transition-colors"><RotateCcw size={12} /></button>
          )}
        </div>
      </div>
      {/* Defect quantity — inspector-entered count for this area only. */}
      <div className="mb-2 flex items-center gap-2">
        <label htmlFor={`qty-${area.compKey}`} className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500 whitespace-nowrap">{area.label} defect qty</label>
        <input
          id={`qty-${area.compKey}`}
          data-testid={`qty-${area.compKey}`}
          type="number"
          min="0"
          step="1"
          value={qtyVal === undefined || qtyVal === null ? '' : qtyVal}
          onChange={(e) => setForm(prev => ({ ...prev, [qtyField]: e.target.value }))}
          placeholder="0"
          className="w-20 text-right px-2 py-1 border border-[#E2E2DF] rounded text-sm font-mono-num focus:outline-none focus:border-[#D35400]"
        />
        {count > 0 && qtyVal === '' && (
          <span className="text-[10px] text-slate-400 italic">PDF uses auto count ({count}) until set</span>
        )}
      </div>
      {/* Inline pre-scripted phrase dropdown. */}
      <div className="mb-2">
        <select
          data-testid={`script-${area.compKey}`}
          value={multiPick ? '' : (selectedScriptId[area.field] || '')}
          onChange={(e) => { insertScriptIntoField(area.field, e.target.value); if (multiPick) e.target.value = ''; }}
          className="w-full px-2.5 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white focus:outline-none focus:border-[#316536]"
        >
          <option value="">{opts.length ? `${multiPick ? 'Add' : 'Choose'} comment… (${opts.length} options)` : 'No prescripted comments for this roof type'}</option>
          {opts.map(s => (
            <option key={s._id} value={s._id}>{s.name}{s.roof_type ? ` · ${s.roof_type}` : ''}</option>
          ))}
        </select>
      </div>
      <textarea
        data-testid={`condition-${area.compKey}`}
        value={form[area.field] || ''}
        onChange={e => setField(area.field, e.target.value)}
        rows={4}
        placeholder={`Notes on the ${area.label.toLowerCase()}s — pull a phrase from the library or type your own…`}
        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm leading-relaxed focus:outline-none focus:border-[#316536] bg-white"
      />
    </div>
  );

  if (showAudit) {
    return (
      <div className="md:col-span-2 grid grid-cols-1 xl:grid-cols-2 gap-3">
        {cardBody}
        <ConditionScriptAuditPanel
          area={{ key: area.scriptCategory, label: area.label }}
          api={api}
          currentRoofKey={roofKey}
          onScriptsChanged={onScriptsChanged}
          LibraryEditModal={LibraryEditModal}
        />
      </div>
    );
  }
  return cardBody;
}

// Link out to the Free Appraisal form, pre-linked to this appraisal's client + property.
function InstantFreeSwitch({ appraisal, appraisalId }) {
  const navigate = useNavigate();
  if (!appraisal?.property_id || !appraisal?.client_id) return null;
  if (appraisal.appraisal_type === 'free_estimate' || appraisal.quote_type_ref === 'Instant Free') return null;
  const url = `/free-appraisal?client_id=${appraisal.client_id}&property_id=${appraisal.property_id}&from_appraisal_id=${appraisalId}`;
  const embed = new URLSearchParams(window.location.search).get('embed');
  const go = (e) => { if (!embed) { e.preventDefault(); navigate(url); } };
  return (
    <div data-testid="instant-free-switch" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs text-sky-900">
      <span>Only need a desktop estimate (Google Earth, no site visit)?</span>
      <a href={url} target={embed ? '_blank' : undefined} rel="noreferrer" onClick={go} data-testid="instant-free-switch-link"
        className="font-semibold text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline">Switch to Instant Free &rarr;</a>
    </div>
  );
}
